import { ReactNode, useRef, useSyncExternalStore } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '__components/Modals/presets/ConfirmationModal';
import LegacyButton from '__components/Button';
import type {
  ServerMessage,
  UserLocation,
} from 'ts-components/PiChat/protocol';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import { sendResultUpload } from 'ts-components/PiChat/presignedPut';
import { useFrameHandler } from '../useFrameHandler';
import {
  createKiCodeNavigation,
  locationPath,
  type NavigateControllerState,
} from './navigateController';
import { navigationGuardRegistry } from 'ts-components/NavigationGuard/navigationGuards';

export interface KiCodeNavigation {
  handleServerMessage: (message: ServerMessage, meta: PiChatFrameMeta) => void;
  getLocation?: () => UserLocation | undefined;
  element: ReactNode;
}

const IDLE: NavigateControllerState = { status: 'idle' };
const subscribeNone = () => () => {};
const getIdle = () => IDLE;
const getPath = () => locationPath(window.location);

export const useKiCodeNavigation = (enabled: boolean): KiCodeNavigation => {
  const { t } = useTranslation();
  const history = useHistory();
  const latest = useRef(history);
  latest.current = history;

  const { instance: wiring, handle: handleServerMessage } = useFrameHandler(
    enabled,
    () =>
      createKiCodeNavigation({
        getPath,
        registry: navigationGuardRegistry,
        push: (path) => latest.current.push(path),
        sendResult: sendResultUpload,
      })
  );

  const controller = wiring?.controller;

  const state = useSyncExternalStore(
    controller?.subscribe ?? subscribeNone,
    controller?.getState ?? getIdle
  );

  const element = (
    <ConfirmationModal
      show={state.status === 'confirming'}
      heading={t('You Have Unsaved Changes')}
      onHide={() => controller?.cancel()}
      leftBtn={
        <LegacyButton
          variant="text"
          color="blue"
          data-qa-id="kicode-navigate-cancel"
        />
      }
      defaultLeftBtnText={t('Cancel')}
      actionButton={false}
      rightBtn={
        <LegacyButton color="red" data-qa-id="kicode-navigate-discard">
          {t('Discard Changes')}
        </LegacyButton>
      }
      rightBtnClickHandler={() => controller?.confirm()}
    >
      {t('Unsaved changes will be lost, would you like to continue?')}
    </ConfirmationModal>
  );

  return { handleServerMessage, getLocation: wiring?.getLocation, element };
};
